import {
  Component,
  ChangeDetectionStrategy,
  input,
  computed,
  output,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { toIsoCurrency } from '../../../../../../shared/utils/xtm-currency.utils';
import { FormatCurrencyPipe } from '../../../../../../shared/pipes/format-currency.pipe';
import {
  Estimate,
  Quote,
  VendorOrder,
} from '../../../../../../shared/models/vendor-order.model';
import { VendorSelectBtnComponent } from '../../../../../../components/vendor-select-btn/vendor-select-btn.component';
import { formatCancellationReason } from '../../../../../../shared/utils/cancellation-reason.utils';
import {
  QuoteType,
  getAvailableQuoteTypes,
  getDeliveryDateForQuoteType,
  getQuoteForType,
} from '../quote-types';

interface QuoteCardRow {
  jobType: QuoteType;
  deliveryDate: string;
  estimatedCost: number;
  currency: string;
  consideration: string | null;
  considerationIcon: 'lowest' | 'warning' | null;
}

const STATUS_MAP: Record<string, { label: string; cssClass: string }> = {
  AWAITING_ESTIMATION: {
    label: 'Pending quote estimates',
    cssClass: 'awaiting-estimation',
  },
  VENDOR_CREATED: {
    label: 'Pending quote estimates',
    cssClass: 'awaiting-estimation',
  },
  AWAITING_VENDOR_SELECTION: {
    label: 'Approved',
    cssClass: 'awaiting-selection',
  },
  AWAITING_VENDOR_APPROVAL: {
    label: 'Awaiting Vendor Approval',
    cssClass: 'awaiting-selection',
  },
  ON_HOLD: { label: 'On Hold', cssClass: 'on-hold' },
  REJECTED: { label: 'Rejected', cssClass: 'rejected' },
  NOT_AVAILABLE: { label: 'Not Available', cssClass: 'rejected' },
  CANCELLED: { label: 'Cancelled', cssClass: 'rejected' },
  COMPLETED: { label: 'Completed', cssClass: 'completed' },
  IN_PROGRESS: { label: 'In Progress', cssClass: 'in-progress' },
};

@Component({
  selector: 'app-quote-card',
  standalone: true,
  imports: [FormatCurrencyPipe, MatIcon, VendorSelectBtnComponent],
  templateUrl: './quote-card.component.html',
  styleUrl: './quote-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuoteCardComponent {
  readonly estimate = input.required<Estimate>();
  readonly orderDeadline = input<string>();
  readonly isLowestStandard = input(false);
  readonly readonlyMode = input(false);
  readonly isPending = input(false);
  readonly pendingStatusLabel = input<string | null>(null);
  readonly pendingMessage = input('Pending quote');
  readonly cancellationReason = input<string | null>(null);
  readonly order = input.required<VendorOrder>();
  readonly viewDetails = output<Estimate>();
  readonly quoteConfirmed = output<string>();

  readonly vendorName = computed(
    () => this.estimate().name ?? 'Unknown Vendor',
  );

  readonly statusLabel = computed(() => {
    if (this.estimate().rejection) {
      return 'Rejected';
    }
    const status = this.estimate().jobStatus ?? '';
    return STATUS_MAP[status]?.label ?? status;
  });

  readonly statusClass = computed(() => {
    const status = this.estimate().jobStatus ?? '';
    return STATUS_MAP[status]?.cssClass ?? 'awaiting-estimation';
  });

  readonly isCancelled = computed(
    () =>
      !this.estimate().rejection &&
      !this.estimate().quote &&
      (this.estimate().jobStatus === 'CANCELLED' ||
        this.order().jobStatus === 'CANCELLED'),
  );

  readonly cancellationReasonLabel = computed(() =>
    formatCancellationReason(this.cancellationReason()),
  );

  readonly formattedCancellationDate = computed(() => {
    const dateValue = this.order().cancellationDate;
    if (!dateValue) return '--';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '--';
    return this.formatUtcDateTimeDDMonYYYY(date);
  });

  readonly hasVendorSelection = computed(
    () =>
      this.order().estimates?.some(
        (e) => e.jobStatus === 'AWAITING_VENDOR_SELECTION',
      ) ?? false,
  );

  readonly hasVendorRejection = computed(
    () =>
      this.order().estimates?.some((e) => e.jobStatus === 'REJECTED') ?? false,
  );
  readonly rejectedReason = computed(() => {
    const reasonMap: Record<string, string> = {
      LACK_RESOURCES:
        'Does not have resources available to translate this job.',
      OTHER: 'Other',
    };
    const code = this.estimate().rejection?.rejectedCode ?? 'OTHER';
    return reasonMap[code] ?? 'Other';
  });
  readonly rejectedDetails = computed(
    () => this.estimate().rejection?.rejectedDetails?.trim() ?? '',
  );
  readonly rejectionDate = computed(() => {
    const value = this.estimate().rejection?.rejectionDate;
    if (!value) return '--';
    if (this.order().jobStatus === 'CANCELLED') {
      return this.formatUtcDateTimeDDMonYYYY(value);
    }
    return this.formatUtcDateDDMonYYYY(value);
  });

  readonly responseDeadline = computed(() => {
    const acceptedDate = this.estimate().quote?.acceptedDate?.trim();
    if (!acceptedDate) return '--';
    const expiry = new Date(acceptedDate);
    expiry.setUTCDate(expiry.getUTCDate() + 30);
    return this.formatUtcDateDDMonYYYY(expiry);
  });

  readonly rows = computed<QuoteCardRow[]>(() => {
    const est = this.estimate();
    const targetDate = this.getTargetDate();

    return getAvailableQuoteTypes(est, true).map((quoteType) => {
      const quote = getQuoteForType(est, quoteType);
      const deliveryDate = this.resolveDeliveryDate(est, quoteType);
      const currency = toIsoCurrency(
        est.currency ??
          (quote as Quote | null)?.currency ??
          est.quote?.normal?.currency,
      );

      return {
        jobType: quoteType,
        deliveryDate: deliveryDate
          ? this.formatDateDDMonYYYY(deliveryDate)
          : '--',
        estimatedCost: quote?.totalCost ?? est.priceMax ?? est.priceMin ?? 0,
        currency,
        consideration: this.getConsideration(
          quoteType,
          deliveryDate,
          targetDate,
        ),
        considerationIcon: this.getConsiderationIcon(
          quoteType,
          deliveryDate,
          targetDate,
        ),
      };
    });
  });

  private getTargetDate(): string | undefined {
    return this.order().fixedDeadline ?? this.order().deadline;
  }

  private resolveDeliveryDate(
    estimate: Estimate,
    quoteType: QuoteType,
  ): Date | string | undefined {
    return getDeliveryDateForQuoteType(estimate, quoteType);
  }

  private getConsideration(
    jobType: QuoteType,
    deliveryDate?: Date | string,
    targetDate?: Date | string,
  ): string | null {
    if (jobType === 'Standard' && this.isLowestStandard()) {
      return 'Lowest Standard Quote';
    }
    if (this.isDeliveryAfterTarget(deliveryDate, targetDate)) {
      return 'Does Not Meet Target Date';
    }
    return null;
  }

  private getConsiderationIcon(
    jobType: QuoteType,
    deliveryDate?: Date | string,
    targetDate?: Date | string,
  ): 'lowest' | 'warning' | null {
    if (jobType === 'Standard' && this.isLowestStandard()) {
      return 'lowest';
    }
    if (this.isDeliveryAfterTarget(deliveryDate, targetDate)) {
      return 'warning';
    }
    return null;
  }

  private isDeliveryAfterTarget(
    deliveryDate?: Date | string,
    targetDate?: Date | string,
  ): boolean {
    if (!deliveryDate || !targetDate) return false;

    const deliveryDay = this.toStartOfDayTimestamp(deliveryDate);
    const targetDay = this.toStartOfDayTimestamp(targetDate);
    if (deliveryDay === null || targetDay === null) return false;

    return deliveryDay > targetDay;
  }

  private toStartOfDayTimestamp(dateValue: Date | string): number | null {
    const parsedDate = new Date(dateValue);

    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    parsedDate.setHours(0, 0, 0, 0);
    return parsedDate.getTime();
  }

  private formatDateDDMonYYYY(dateValue: Date | string): string {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '--';
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const day = date.getDate().toString().padStart(2, '0');
    return `${day}/${months[date.getMonth()]}/${date.getFullYear()}`;
  }

  private formatUtcDateDDMonYYYY(dateValue: Date | string): string {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '--';
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const day = date.getUTCDate().toString().padStart(2, '0');
    return `${day}/${months[date.getUTCMonth()]}/${date.getUTCFullYear()}`;
  }

  private formatUtcDateTimeDDMonYYYY(dateValue: Date | string): string {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '--';

    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const day = date.getUTCDate().toString().padStart(2, '0');
    const hours24 = date.getUTCHours();
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    const period = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 % 12 || 12;

    return `${hours12}:${minutes} ${period} ${day}/${months[date.getUTCMonth()]}/${date.getUTCFullYear()}`;
  }

  shouldShowDetailsBtn(): boolean {
    const quote = this.estimate().quote as
      | {
          acceptedBy?: string;
          acceptedDate?: string;
          normal?: { acceptedBy?: string; acceptedDate?: string };
          rushed?: { acceptedBy?: string; acceptedDate?: string };
        }
      | undefined;

    const acceptedBy =
      quote?.acceptedBy?.trim() ??
      quote?.normal?.acceptedBy?.trim() ??
      quote?.rushed?.acceptedBy?.trim();
    const acceptedDate =
      quote?.acceptedDate?.trim() ??
      quote?.normal?.acceptedDate?.trim() ??
      quote?.rushed?.acceptedDate?.trim();

    return (
      this.statusLabel() === 'Approved' || (!!acceptedBy && !!acceptedDate)
    );
  }
}
